import React, { useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { getvariantQueryMode } from "../../../state/selectors/general.selectors";
import EmptyStateChart from "./EmptyStateChart";
import BarChart from "../BarChart/BarChart";
import Modal from "../Modal";
import Btn from "../Btn";
import PieChart from "../PieChart";
import SingleValueChart from "./SingleValueChart";
import {
  highlightChartColor,
  neutralChartColor,
} from "../../../common/helpers/colors";
import {
  ETHINICITES_ABBREVIATIONS_TO_ETHNICITIES as ethnAbrDict,
  SEX_ABBREVIATIONS_TO_SEX as sexAbrDict,
} from "../../../common/helpers/Constant";
import withErrorGuard from "../../../HOC/withErrorGuard";

function truncateText(str, len) {
  if (str.length > len + 5) return `${str.substring(0, len)}...`;
  return str;
}

function calcItems(data = [], maxNum, queryMode, getNormalizedLabel) {
  const cancers = queryMode?.cancer_type || [];
  const tissues = queryMode?.tissue_type || [];
  const age = queryMode?.age || -1;
  const ethn = ethnAbrDict[queryMode?.ethnicity];
  const sex = sexAbrDict[queryMode?.sex];

  const termSet = [...cancers, ...tissues, ethn, sex, age]
    .filter(Boolean)
    .map((x) => getNormalizedLabel(String(x)))
    .reduce((a, i) => a.add(i), new Set());

  const items = data.filter((x) => x?.value > -1);
  const fitting = items.slice(0, maxNum);
  const rest = items.slice(maxNum);
  const restSum = rest.reduce((a, x) => a + x.value, 0);
  const average = restSum / rest.length;
  const niceAverage = Math.round(average * 100) / 100;
  const averageTitle = `${niceAverage} on average`;
  const other = {
    key: `${rest.length} more`,
    value: average,
    title: averageTitle,
    children: rest,
  };
  return [...fitting, other]
    .filter((x) => x && x.key && x.value > -1)
    .map(({ key, value, children, title }) => {
      const label = key;
      const id = String(key).toLowerCase();
      const color = termSet.has(id) ? highlightChartColor : neutralChartColor;
      const truncatedLabel = truncateText(key, 15);
      return { key, label, truncatedLabel, value, children, color, title };
    });
}

function Chart({
  title,
  showEmptyState,
  data,
  queryMode,
  limit,
  getNormalizedLabel,
}) {
  const [modalData, setModalData] = useState(null);
  const items = calcItems(data || [], limit, queryMode, getNormalizedLabel);
  const itemCount = items.length;

  if (itemCount === 0 && !showEmptyState) {
    return null;
  }

  if (itemCount === 0 && showEmptyState) {
    return <EmptyStateChart type="bar" name={title} />;
  }

  if (itemCount === 1) {
    return (
      <div className="chart">
        <SingleValueChart title={title} data={data} />
      </div>
    );
  }

  if (itemCount < 5) {
    return (
      <div className="chart">
        <PieChart title={title} data={items} />
      </div>
    );
  }

  return (
    <>
      <div className="chart">
        <BarChart
          minHeight={80}
          items={items}
          title={title}
          getLabel={(x) => x.truncatedLabel}
          isInModal={false}
        />

        <Btn
          className="tw-absolute tw-top-0 tw-right-0 tw-border tw-rounded tw-border-saphetorBlue tw-m-1 tw-py-px tw-px-1"
          onSubmit={() =>
            setModalData(calcItems(data, 40, queryMode, getNormalizedLabel))
          }
        >
          <i className="fa fa-expand tw-text-saphetorBlue tw-fill-current tw-mx-1" />
        </Btn>
      </div>

      <Modal
        isOpen={Boolean(modalData)}
        onRequestClose={() => setModalData(null)}
        ContentRender={() => {
          return (
            <div className="tw-max-w-screen90 tw-p-4 tw-mt-3">
              <BarChart
                minHeight={260}
                items={modalData}
                title={title}
                getLabel={(x) => x.label}
                isInModal={true}
              />
            </div>
          );
        }}
      />
    </>
  );
}

Chart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  title: PropTypes.string.isRequired,
  showEmptyState: PropTypes.bool,
  queryMode: PropTypes.shape({}),
  limit: PropTypes.number,
  getNormalizedLabel: PropTypes.func,
};

Chart.defaultProps = {
  getNormalizedLabel: (x) => x.toLowerCase(),
  data: [],
  showEmptyState: true,
  queryMode: {},
  limit: 5,
};

const mapStateToProps = (state) => {
  return { queryMode: getvariantQueryMode(state) };
};

export default withErrorGuard(connect(mapStateToProps)(Chart));
